import {
  getOmniSearch,
  getOmniSearchEnterprise,
  UserKeys,
} from '../../api/users-api';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import useDebounce from '../../utils/useDebounce';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  allPages,
  gmailSettingsPages,
  groupDetailPages,
  PageConfigItem,
  pagesConfig,
  PagesConfigKey,
  userDetailPages,
} from '../../shared/pages-config';
import { useStore } from '../state/zustand-store';
import { isFeatureEnabled } from '../../utils/feature-flags';
import { grey } from '@mui/material/colors';
import SearchListWrapper from './search-list-wrapper';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import UserAvatar from '../../shared/user-avatar';
import { useUserSettings } from '../../utils/use-user-settings';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import InputAdornment from '@mui/material/InputAdornment';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import Business from '@mui/icons-material/Business';
import People from '@mui/icons-material/People';
import Search from '@mui/icons-material/Search';
import Close from '@mui/icons-material/Close';

interface SearchDialogProps {
  open: boolean;
  handleClose: () => void;
}

interface RecentUserSearch {
  email: string;
  name: string;
  dateSearched: number;
  orgUnit?: string;
  isAlias: boolean;
}

interface RecentGroupSearch {
  email: string;
  name: string;
  dateSearched: number;
  memberCount?: number;
  isAlias: boolean;
}

interface RecentSection extends PageConfigItem {
  dateSearched: number;
}

export default function SearchDialog({ open, handleClose }: SearchDialogProps) {
  const { activeLabs, features } = useUserSettings();
  const [searchText, setSearchText] = useState('');
  const { colorMode } = useStore();
  const debouncedInput = useDebounce(searchText, 200);
  const [recentUserSearches, setRecentUserSearches] = useState<
    RecentUserSearch[]
  >(() => {
    const recentSearchesString = localStorage.getItem('recentUserSearches');
    const recentSearchArray = recentSearchesString
      ? (JSON.parse(recentSearchesString) as RecentUserSearch[])
      : [];
    return recentSearchArray.sort((a, b) => b.dateSearched - a.dateSearched);
  });
  const [recentGroupSearches, setRecentGroupSearches] = useState<
    RecentGroupSearch[]
  >(() => {
    const recentSearchesString = localStorage.getItem('recentGroupSearches');
    const recentSearchArray = recentSearchesString
      ? (JSON.parse(recentSearchesString) as RecentGroupSearch[])
      : [];
    return recentSearchArray.sort((a, b) => b.dateSearched - a.dateSearched);
  });
  const [recentSections, setRecentSections] = useState<RecentSection[]>(() => {
    const recentSearchesString = localStorage.getItem('recentSections');
    const recentSearchArray = recentSearchesString
      ? (JSON.parse(recentSearchesString) as RecentSection[])
      : [];
    const items = recentSearchArray.map((section) => {
      return {
        ...section,
        icon: pagesConfig[section.value as PagesConfigKey].icon,
      };
    });
    return items.sort((a, b) => b.dateSearched - a.dateSearched);
  });
  const [recentUserSubSections, setRecentUserSubSections] = useState<
    RecentSection[]
  >(() => {
    const recentSearchesString = localStorage.getItem('recentUserSubSections');
    const recentSearchArray = recentSearchesString
      ? (JSON.parse(recentSearchesString) as RecentSection[])
      : [];
    const items = recentSearchArray.map((section) => {
      return {
        ...section,
        icon: pagesConfig[section.value as PagesConfigKey].icon,
      };
    });
    return items.sort((a, b) => b.dateSearched - a.dateSearched);
  });
  const navigate = useNavigate();
  const location = useLocation();

  const { userEmail, groupEmail } = useParams() as {
    userEmail: string | undefined;
    groupEmail: string | undefined;
  };

  // Recent sections are stored in localStorage, so use the live page config
  // rather than the saved copy when deciding whether a feature flag still allows them.
  const isSectionEnabled = (section: { value: string }) => {
    const page: PageConfigItem | undefined =
      pagesConfig[section.value as PagesConfigKey];
    return (
      !page?.requiredFeatureFlag ||
      isFeatureEnabled(page.requiredFeatureFlag, features)
    );
  };

  const visibleRecentSections = recentSections.filter(isSectionEnabled);
  const visibleRecentUserSubSections =
    recentUserSubSections.filter(isSectionEnabled);

  const { data } = useQuery({
    queryKey: [UserKeys.OmniSearchResults, debouncedInput],
    queryFn: () =>
      isFeatureEnabled('indexUsersReact', features)
        ? getOmniSearchEnterprise(debouncedInput)
        : getOmniSearch(debouncedInput),
    enabled: debouncedInput.length > 0,
    placeholderData: keepPreviousData,
  });

  const allPagesWithoutDetailPages = allPages.filter(
    (page) =>
      !page.isUserDetails &&
      !page.isGroupDetails &&
      !page.isBillingDetails &&
      !page.isIndexingDetails &&
      !page.isDecomDetails &&
      !page.isRoleDetails &&
      !page.isViewAdmin &&
      !page.hiddenFromSearch &&
      isSectionEnabled(page) &&
      !(
        page.value === 'gpanelContacts' &&
        !activeLabs.includes('gpanelcontacts')
      ) &&
      !(
        page.value === 'vaultExport' &&
        !isFeatureEnabled('vaultExportReact', features)
      )
  );

  const allGpanelSections = [
    ...allPagesWithoutDetailPages,
    pagesConfig.indexing,
    pagesConfig.billing,
    pagesConfig.decommissioningLayout,
  ];

  const pagesShown = [...allGpanelSections].flat();

  const userPages = userEmail
    ? [...userDetailPages, ...gmailSettingsPages]
    : [];
  const groupPages = groupEmail ? groupDetailPages : [];

  const filteredSections = pagesShown
    .sort((a, b) => a.name.localeCompare(b.name))
    .filter((section) =>
      section.name.toLocaleLowerCase().includes(searchText.toLocaleLowerCase())
    );

  const filteredUserSections = userPages.filter(
    (section) =>
      isSectionEnabled(section) &&
      section.name.toLocaleLowerCase().includes(searchText.toLocaleLowerCase())
  );

  const filteredGroupSections = groupPages.filter(
    (section) =>
      isSectionEnabled(section) &&
      section.name.toLocaleLowerCase().includes(searchText.toLocaleLowerCase())
  );

  const { users, aliases, groups, groupAliases } = data || {};

  const modifiedUsers =
    users?.map((user) => {
      return {
        email: user.userEmail,
        name: `${user.firstName} ${user.lastName}`,
        orgUnit: user.orgUnitPath,
        isAlias: false,
      };
    }) || [];

  const modifiedAliases =
    aliases?.map((alias) => {
      return {
        email: alias.aliasEmail,
        name: `${alias.firstName} ${alias.lastName}`,
        orgUnit: undefined,
        isAlias: true,
      };
    }) || [];

  const usersAndAliases = [...modifiedUsers, ...modifiedAliases];

  const modifiedGroups =
    groups?.map((group) => {
      return {
        email: group.email,
        name: group.name,
        memberCount: group.directMemberCount,
        isAlias: false,
      };
    }) || [];
  const modifiedGroupAliases =
    groupAliases?.map((groupAlias) => {
      return {
        email: groupAlias.aliasEmail,
        name: groupAlias.name,
        memberCount: undefined,
        isAlias: true,
      };
    }) || [];

  const groupsAndGroupAliases = [...modifiedGroups, ...modifiedGroupAliases];

  function handleRecentUserSection(searchToAdd: RecentUserSearch) {
    const recentSearch = recentUserSearches.find(
      (search) => search.email === searchToAdd.email
    );
    if (recentSearch) {
      const filteredRecentSearches = recentUserSearches.filter(
        (search) => search.email !== searchToAdd.email
      );
      localStorage.setItem(
        'recentUserSearches',
        JSON.stringify([searchToAdd, ...filteredRecentSearches])
      );
      setRecentUserSearches([searchToAdd, ...filteredRecentSearches]);
    } else {
      if (recentUserSearches.length === 4) {
        const updatedRecentSearches = recentUserSearches.slice(0, 3);
        localStorage.setItem(
          'recentUserSearches',
          JSON.stringify([searchToAdd, ...updatedRecentSearches])
        );
        setRecentUserSearches(() => [searchToAdd, ...updatedRecentSearches]);
      } else {
        localStorage.setItem(
          'recentUserSearches',
          JSON.stringify([searchToAdd, ...recentUserSearches])
        );
        setRecentUserSearches((prev) => [searchToAdd, ...prev]);
      }
    }
  }

  function handleRecentGroupSection(searchToAdd: RecentGroupSearch) {
    const recentSearch = recentGroupSearches.find(
      (search) => search.email === searchToAdd.email
    );
    if (recentSearch) {
      const filteredRecentSearches = recentGroupSearches.filter(
        (search) => search.email !== searchToAdd.email
      );
      localStorage.setItem(
        'recentGroupSearches',
        JSON.stringify([searchToAdd, ...filteredRecentSearches])
      );
      setRecentGroupSearches([searchToAdd, ...filteredRecentSearches]);
    } else {
      if (recentGroupSearches.length === 4) {
        const updatedRecentSearches = recentGroupSearches.slice(0, 3);
        localStorage.setItem(
          'recentGroupSearches',
          JSON.stringify([searchToAdd, ...updatedRecentSearches])
        );
        setRecentGroupSearches(() => [searchToAdd, ...updatedRecentSearches]);
      } else {
        localStorage.setItem(
          'recentGroupSearches',
          JSON.stringify([searchToAdd, ...recentGroupSearches])
        );
        setRecentGroupSearches((prev) => [searchToAdd, ...prev]);
      }
    }
  }

  function handleRecentSections(sectionToAdd: RecentSection) {
    const recentSection = sectionToAdd.isUserDetails
      ? recentUserSubSections.find(
          (section) => section.value === sectionToAdd.value
        )
      : recentSections.find((section) => section.value === sectionToAdd.value);
    if (recentSection) {
      if (!recentSection.isUserDetails) {
        const filteredRecentSections = recentSections.filter(
          (section) => section.value !== sectionToAdd.value
        );
        localStorage.setItem(
          'recentSections',
          JSON.stringify([sectionToAdd, ...filteredRecentSections])
        );
        setRecentSections([sectionToAdd, ...filteredRecentSections]);
      } else {
        const filteredRecentSections = recentUserSubSections.filter(
          (section) => section.value !== sectionToAdd.value
        );
        localStorage.setItem(
          'recentUserSubSections',
          JSON.stringify([sectionToAdd, ...filteredRecentSections])
        );
        setRecentUserSubSections([sectionToAdd, ...filteredRecentSections]);
      }
    } else {
      if (!sectionToAdd.isUserDetails) {
        if (recentSections.length === 5) {
          const updatedRecentSections = recentSections.slice(0, 4);

          localStorage.setItem(
            'recentSections',
            JSON.stringify([sectionToAdd, ...updatedRecentSections])
          );

          setRecentSections(() => [sectionToAdd, ...updatedRecentSections]);
        } else {
          localStorage.setItem(
            'recentSections',
            JSON.stringify([sectionToAdd, ...recentSections])
          );
          setRecentSections((prev) => [sectionToAdd, ...prev]);
        }
      } else {
        if (recentUserSubSections.length === 5) {
          const updatedRecentSections = recentUserSubSections.slice(0, 4);

          localStorage.setItem(
            'recentUserSubSections',
            JSON.stringify([sectionToAdd, ...updatedRecentSections])
          );

          setRecentUserSubSections(() => [
            sectionToAdd,
            ...updatedRecentSections,
          ]);
        } else {
          localStorage.setItem(
            'recentUserSubSections',
            JSON.stringify([sectionToAdd, ...recentUserSubSections])
          );
          setRecentUserSubSections((prev) => [sectionToAdd, ...prev]);
        }
      }
    }
  }

  function handleNavigation(
    searchType: 'groups' | 'users',
    searchedEmail: string
  ) {
    const path = location.pathname;
    const pathSegments = path.split('/');
    const searchPrefix = searchType === 'users' ? 'users' : 'groups';

    const indexOfTypeSegment = pathSegments.findIndex(
      (segment) => segment === searchPrefix
    );

    const segmentsAfterType =
      indexOfTypeSegment !== -1
        ? pathSegments.slice(indexOfTypeSegment + 2)
        : [];
    const hasSegmentsAfterType = segmentsAfterType.length > 0;

    const isSwitchingType = pathSegments.some((segment) => {
      return (
        (segment === 'users' && searchType === 'groups') ||
        (segment === 'groups' && searchType === 'users')
      );
    });

    const newPath = `/${searchPrefix}/${searchedEmail}`;
    const additionalSegments =
      hasSegmentsAfterType && !isSwitchingType
        ? `/${segmentsAfterType.join('/')}`
        : '';

    navigate(newPath + additionalSegments);
  }

  function handleChipNavigation(section: PageConfigItem) {
    if (section.isGmailSettings && userEmail) {
      return `/${pagesConfig.users.link}/${userEmail}/${pagesConfig.gmailSettings.link}/${section.link}`;
    }
    if (section.isUserDetails && userEmail) {
      return `/${pagesConfig.users.link}/${userEmail}/${section.link}`;
    } else if (section.isGroupDetails && groupEmail) {
      return `/${pagesConfig.groups.link}/${groupEmail}/${section.link}`;
    } else if (section.isCalendarDetails) {
      return `/${pagesConfig.resourceCalendarsLayout.link}/${section.link}`;
    } else if (section.isAppSettings) {
      return `/${pagesConfig.settings.link}/${section.link}`;
    } else if (section.isDecomDetails) {
      return `/${pagesConfig.decommissioningLayout.link}/${section.link}`;
    } else if (section.isIndexingDetails) {
      return `/${pagesConfig.indexing.link}/${section.link}`;
    } else if (section.isBillingDetails) {
      return `/${pagesConfig.billing.link}/${section.link}`;
    }

    return section.link;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      disableRestoreFocus
    >
      <DialogTitle>
        <TextField
          placeholder="Search for users, groups, aliases, or gPanel sections"
          variant="outlined"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search color="primary" />
              </InputAdornment>
            ),
            endAdornment: searchText ? (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setSearchText('')}
                  edge="end"
                  size="small"
                >
                  <Close fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          fullWidth
          autoFocus
          autoComplete="off"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </DialogTitle>
      <DialogContent sx={{ minHeight: 350, width: '100%' }}>
        <Stack gap={1} sx={{ width: '100%' }}>
          <Divider />
          {searchText.length === 0 &&
          visibleRecentSections.length === 0 &&
          recentUserSearches.length === 0 &&
          recentGroupSearches.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                height: 300,
                bgcolor: colorMode == 'light' ? grey[100] : grey[900],
                padding: 3,
                borderRadius: 2,
              }}
            >
              <Stack flexDirection="column" alignItems="center" spacing={2}>
                <Stack
                  flexDirection="row"
                  alignItems="center"
                  justifyContent="center"
                  gap={1}
                >
                  <Search />
                  <Typography variant="h5" fontWeight="bold">
                    Navigate by search
                  </Typography>
                </Stack>
                <Typography variant="body1" px={6} textAlign="center">
                  Use the search bar above to find users, groups, or sections.
                  We'll guide you to the right place.
                </Typography>
              </Stack>
            </Box>
          ) : (
            <Stack>
              {groupEmail ? (
                <SearchListWrapper sectionName="Group Sections">
                  {filteredGroupSections.length > 0 ? (
                    filteredGroupSections.slice(0, 5).map((section) => {
                      return (
                        <Chip
                          component={Link}
                          key={section.value}
                          label={
                            filteredGroupSections
                              .slice(0, 5)
                              .filter((s) => s.name === section.name).length > 1
                              ? section.name
                              : section.name
                          }
                          sx={{ width: 'fit-content' }}
                          icon={<section.icon />}
                          size="small"
                          color="primary"
                          clickable
                          to={handleChipNavigation(section)}
                          onClick={() => {
                            handleClose();
                          }}
                        />
                      );
                    })
                  ) : (
                    <Box sx={{ height: 24 }}>
                      <Typography variant="body2">
                        No group sections found
                      </Typography>
                    </Box>
                  )}
                </SearchListWrapper>
              ) : null}
              {searchText.length === 0 ? (
                <Stack>
                  {userEmail ? (
                    <SearchListWrapper sectionName="Recent User Sections">
                      {visibleRecentUserSubSections.length > 0 ? (
                        visibleRecentUserSubSections.map((section) => {
                          return (
                            <Chip
                              component={Link}
                              label={section.name}
                              key={section.value}
                              sx={{
                                width: 'fit-content',
                                backgroundColor: '-moz-initial',
                              }}
                              icon={<section.icon />}
                              size="small"
                              clickable
                              color="primary"
                              to={handleChipNavigation(section)}
                              onClick={() => {
                                const sectionToAdd = {
                                  ...section,
                                  dateSearched: Date.now(),
                                };
                                handleRecentSections(sectionToAdd);
                                handleClose();
                              }}
                            />
                          );
                        })
                      ) : (
                        <Box sx={{ height: 24 }}>
                          <Typography variant="body2">
                            No user sections found
                          </Typography>
                        </Box>
                      )}
                    </SearchListWrapper>
                  ) : null}
                  <SearchListWrapper sectionName="Recent Sections">
                    {visibleRecentSections.length > 0 ? (
                      visibleRecentSections.map((section) => {
                        return (
                          <Tooltip key={section.value} title={section.name}>
                            <Chip
                              component={Link}
                              label={section.name}
                              sx={{ width: 'fit-content' }}
                              icon={<section.icon />}
                              size="small"
                              color="primary"
                              clickable
                              to={handleChipNavigation(section)}
                              onClick={() => {
                                const sectionToAdd = {
                                  ...section,
                                  dateSearched: Date.now(),
                                };
                                handleRecentSections(sectionToAdd);
                                handleClose();
                              }}
                            />
                          </Tooltip>
                        );
                      })
                    ) : (
                      <Box sx={{ height: 24 }}>
                        <Typography variant="body2">
                          No sections found
                        </Typography>
                      </Box>
                    )}
                  </SearchListWrapper>
                  <Stack>
                    <SearchListWrapper sectionName="Recent Users">
                      {recentUserSearches.length > 0 ? (
                        recentUserSearches.map((search) => {
                          return (
                            <ListItemButton
                              sx={{
                                pl: 1,
                                borderRadius: 1,
                                bgcolor: (theme) =>
                                  theme.palette.background.default,
                                '&:hover': {
                                  bgcolor: (theme) =>
                                    theme.palette.primary.light,
                                },
                                flex: '0 0 400px',
                                maxWidth: '100%',
                                dispay: 'flex',
                              }}
                              key={search.email}
                              dense
                              disableGutters
                              onClick={() => {
                                const userToAdd = {
                                  email: search.email,
                                  name: search.name,
                                  dateSearched: Date.now(),
                                  orgUnit: search.orgUnit
                                    ? search.orgUnit
                                    : undefined,
                                  isAlias: search.isAlias,
                                };
                                handleRecentUserSection(userToAdd);
                                handleNavigation('users', search.email);
                                handleClose();
                              }}
                            >
                              <ListItemAvatar sx={{ minWidth: 36, px: 1 }}>
                                <UserAvatar
                                  userEmail={search.email}
                                  sx={{ width: 30, height: 30 }}
                                  alt={search.name}
                                />
                              </ListItemAvatar>
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  flexGrow: 1,
                                }}
                              >
                                {search.orgUnit &&
                                search.orgUnit.length > 0 &&
                                search.orgUnit !== '/' ? (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      justifyContent: 'center',
                                    }}
                                  >
                                    <ListItemText
                                      id={search.email}
                                      primary={search.email}
                                      secondary={
                                        search.isAlias
                                          ? `Alias of: ${search.name}`
                                          : search.name
                                      }
                                      sx={{ margin: 0 }}
                                    />
                                    <Stack flexDirection={'row'}>
                                      <Business sx={{ fontSize: 15 }} />
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontSize: 12,
                                          ml: 0.5,
                                          color: 'text.primary',
                                        }}
                                      >
                                        {search.orgUnit}
                                      </Typography>
                                    </Stack>
                                  </Box>
                                ) : (
                                  <ListItemText
                                    id={search.email}
                                    primary={search.email}
                                    secondary={
                                      search.isAlias
                                        ? `Alias of: ${search.name}`
                                        : search.name
                                    }
                                  />
                                )}
                              </Box>
                            </ListItemButton>
                          );
                        })
                      ) : (
                        <Box sx={{ height: 24 }}>
                          <Typography variant="body2">
                            No users found
                          </Typography>
                        </Box>
                      )}
                    </SearchListWrapper>
                    <SearchListWrapper sectionName="Recent Groups">
                      {recentGroupSearches.length > 0 ? (
                        recentGroupSearches.map((search) => {
                          return (
                            <ListItemButton
                              sx={{
                                pl: 1,
                                borderRadius: 1,
                                bgcolor: (theme) =>
                                  theme.palette.background.default,
                                '&:hover': {
                                  bgcolor: (theme) =>
                                    theme.palette.primary.light,
                                },
                                flex: '0 0 400px',
                                maxWidth: '100%',
                              }}
                              key={search.email}
                              dense
                              disableGutters
                              onClick={() => {
                                const groupToAdd = {
                                  email: search.email,
                                  name: search.name,
                                  dateSearched: Date.now(),
                                  memberCount: search.memberCount
                                    ? search.memberCount
                                    : undefined,
                                  isAlias: search.isAlias,
                                };

                                handleRecentGroupSection(groupToAdd);
                                handleNavigation('groups', search.email);
                                handleClose();
                              }}
                            >
                              <ListItemAvatar sx={{ minWidth: 36, px: 1 }}>
                                <Avatar
                                  alt={search.name}
                                  sx={{ width: 30, height: 30 }}
                                >
                                  <People />
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                id={search.email}
                                primary={search.email}
                                secondary={
                                  search.memberCount && search.memberCount > 0
                                    ? `${search.memberCount} Members`
                                    : search.memberCount &&
                                      search.memberCount === 0
                                    ? `${search.memberCount} Member`
                                    : `Alias of: ${search.name}`
                                }
                              />
                            </ListItemButton>
                          );
                        })
                      ) : (
                        <Box sx={{ height: 24 }}>
                          <Typography variant="body2">
                            No groups found
                          </Typography>
                        </Box>
                      )}
                    </SearchListWrapper>
                  </Stack>
                </Stack>
              ) : null}
              {searchText.length !== 0 ? (
                <Stack>
                  {userEmail ? (
                    <SearchListWrapper sectionName="User Sections">
                      {filteredUserSections.length > 0 ? (
                        filteredUserSections.slice(0, 5).map((section) => {
                          return (
                            <Chip
                              component={Link}
                              key={section.value}
                              label={
                                filteredUserSections
                                  .slice(0, 5)
                                  .filter((s) => s.name === section.name)
                                  .length > 1
                                  ? section.name
                                  : section.name
                              }
                              sx={{ width: 'fit-content' }}
                              icon={<section.icon />}
                              size="small"
                              color="primary"
                              clickable
                              to={handleChipNavigation(section)}
                              onClick={() => {
                                const sectionToAdd = {
                                  ...section,
                                  dateSearched: Date.now(),
                                };
                                handleRecentSections(sectionToAdd);
                                handleClose();
                              }}
                            />
                          );
                        })
                      ) : (
                        <Box sx={{ height: 24 }}>
                          <Typography variant="body2">
                            No user sections found
                          </Typography>
                        </Box>
                      )}
                    </SearchListWrapper>
                  ) : null}
                  <SearchListWrapper sectionName={'gPanel Sections'}>
                    {filteredSections.length > 0 ? (
                      filteredSections.slice(0, 5).map((section) => {
                        return (
                          <Chip
                            component={Link}
                            key={section.value}
                            label={
                              filteredSections
                                .slice(0, 5)
                                .filter((s) => s.name === section.name).length >
                              1
                                ? section.name
                                : section.name
                            }
                            sx={{ width: 'fit-content' }}
                            icon={<section.icon />}
                            size="small"
                            color="primary"
                            clickable
                            to={handleChipNavigation(section)}
                            onClick={() => {
                              const sectionToAdd = {
                                ...section,
                                dateSearched: Date.now(),
                              };
                              handleRecentSections(sectionToAdd);
                              handleClose();
                            }}
                          />
                        );
                      })
                    ) : (
                      <Box sx={{ height: 24 }}>
                        <Typography variant="body2">
                          No sections found
                        </Typography>
                      </Box>
                    )}
                  </SearchListWrapper>
                  <Stack>
                    <SearchListWrapper sectionName="Users">
                      {users && usersAndAliases.length > 0 ? (
                        usersAndAliases.map((user) => {
                          const emailMatches = match(user.email, searchText, {
                            insideWords: true,
                          });
                          const emailParts = parse(user.email, emailMatches);
                          const nameMatches = match(user.name, searchText, {
                            insideWords: true,
                          });
                          const nameParts = parse(user.name, nameMatches);
                          const orgUnitMatches = user.orgUnit
                            ? match(user.orgUnit, searchText)
                            : [];
                          const orgUnitParts = user.orgUnit
                            ? parse(user.orgUnit, orgUnitMatches)
                            : [];
                          return (
                            <ListItemButton
                              sx={{
                                pl: 1,
                                borderRadius: 1,
                                bgcolor: (theme) =>
                                  theme.palette.background.default,
                                '&:hover': {
                                  bgcolor: (theme) =>
                                    theme.palette.primary.light,
                                },
                                flex: '0 0 400px',
                                maxWidth: '100%',
                              }}
                              key={user.email}
                              dense
                              disableGutters
                              onClick={() => {
                                const userToAdd = {
                                  email: user.email,
                                  name: user.name,
                                  dateSearched: Date.now(),
                                  orgUnit: user.orgUnit
                                    ? user.orgUnit
                                    : undefined,
                                  isAlias: user.isAlias,
                                };

                                handleRecentUserSection(userToAdd);
                                handleNavigation('users', user.email);
                                handleClose();
                              }}
                            >
                              <ListItemAvatar sx={{ minWidth: 36, px: 1 }}>
                                <UserAvatar
                                  userEmail={user.email}
                                  sx={{ width: 30, height: 30 }}
                                  alt={user.name}
                                />
                              </ListItemAvatar>
                              {user.orgUnit &&
                              user.orgUnit.length > 0 &&
                              user.orgUnit !== '/' ? (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                  }}
                                >
                                  <ListItemText
                                    id={user.email}
                                    primary={emailParts.map((part) => (
                                      <span
                                        key={part.text}
                                        style={{
                                          fontWeight: part.highlight
                                            ? 700
                                            : 400,
                                        }}
                                      >
                                        {part.text}
                                      </span>
                                    ))}
                                    secondary={nameParts.map((part) => (
                                      <span
                                        key={part.text}
                                        style={{
                                          fontWeight: part.highlight
                                            ? 700
                                            : 400,
                                        }}
                                      >
                                        {part.text}
                                      </span>
                                    ))}
                                    sx={{ margin: 0 }}
                                  />
                                  <Stack flexDirection={'row'}>
                                    <Business sx={{ fontSize: 15 }} />
                                    {orgUnitParts.map((part) => (
                                      <Typography
                                        key={part.text}
                                        sx={{
                                          fontSize: 12,
                                          fontWeight: part.highlight
                                            ? 700
                                            : 400,
                                        }}
                                      >
                                        {part.text}
                                      </Typography>
                                    ))}
                                  </Stack>
                                </Box>
                              ) : (
                                <ListItemText
                                  id={user.email}
                                  primary={emailParts.map((part) => (
                                    <span
                                      key={part.text}
                                      style={{
                                        fontWeight: part.highlight ? 700 : 400,
                                      }}
                                    >
                                      {part.text}
                                    </span>
                                  ))}
                                  secondary={
                                    <span>
                                      Alias of:{' '}
                                      {nameParts.map((part) => (
                                        <span
                                          key={part.text}
                                          style={{
                                            fontWeight: part.highlight
                                              ? 700
                                              : 400,
                                          }}
                                        >
                                          {part.text}
                                        </span>
                                      ))}
                                    </span>
                                  }
                                />
                              )}
                            </ListItemButton>
                          );
                        })
                      ) : (
                        <Box sx={{ height: 24 }}>
                          <Typography variant="body2">
                            No users found
                          </Typography>
                        </Box>
                      )}
                    </SearchListWrapper>
                    <SearchListWrapper sectionName="Groups">
                      {groups && groupsAndGroupAliases.length > 0 ? (
                        groupsAndGroupAliases.map((group) => {
                          const emailMatches = match(group.email, searchText, {
                            insideWords: true,
                          });
                          const emailParts = parse(group.email, emailMatches);
                          const nameMatches = match(group.name, searchText, {
                            insideWords: true,
                          });
                          const nameParts = parse(group.name, nameMatches);
                          const memberCountMatches = group.memberCount
                            ? match(group.memberCount.toString(), searchText)
                            : [];
                          const memberCountParts = group.memberCount
                            ? parse(
                                group.memberCount.toString(),
                                memberCountMatches
                              )
                            : [];
                          return (
                            <ListItemButton
                              sx={{
                                pl: 1,
                                borderRadius: 1,
                                bgcolor: (theme) =>
                                  theme.palette.background.default,
                                '&:hover': {
                                  bgcolor: (theme) =>
                                    theme.palette.primary.light,
                                },
                                flex: '0 0 400px',
                                maxWidth: '100%',
                              }}
                              key={group.email}
                              dense
                              disableGutters
                              onClick={() => {
                                const groupToAdd = {
                                  email: group.email,
                                  name: group.name,
                                  dateSearched: Date.now(),
                                  memberCount: group.memberCount
                                    ? group.memberCount
                                    : undefined,
                                  isAlias: group.isAlias,
                                };

                                handleRecentGroupSection(groupToAdd);
                                handleNavigation('groups', group.email);
                                handleClose();
                              }}
                            >
                              <ListItemAvatar sx={{ minWidth: 36, px: 1 }}>
                                <Avatar
                                  alt={group.name}
                                  sx={{ width: 30, height: 30 }}
                                >
                                  <People />
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                id={group.email}
                                primary={emailParts.map((part) => (
                                  <span
                                    key={part.text}
                                    style={{
                                      fontWeight: part.highlight ? 700 : 400,
                                    }}
                                  >
                                    {part.text}
                                  </span>
                                ))}
                                secondary={
                                  group.memberCount &&
                                  (group.memberCount > 1 ||
                                    group.memberCount === 0) ? (
                                    <span>
                                      {memberCountParts.map((part) => (
                                        <span
                                          key={part.text}
                                          style={{
                                            fontWeight: part.highlight
                                              ? 700
                                              : 400,
                                          }}
                                        >
                                          {part.text}
                                        </span>
                                      ))}{' '}
                                      Members
                                    </span>
                                  ) : group.memberCount &&
                                    group.memberCount == 1 ? (
                                    <span>
                                      {memberCountParts.map((part) => (
                                        <span
                                          key={part.text}
                                          style={{
                                            fontWeight: part.highlight
                                              ? 700
                                              : 400,
                                          }}
                                        >
                                          {part.text}
                                        </span>
                                      ))}{' '}
                                      Member
                                    </span>
                                  ) : (
                                    <span>
                                      Alias of:{' '}
                                      {nameParts.map((part) => (
                                        <span
                                          key={part.text}
                                          style={{
                                            fontWeight: part.highlight
                                              ? 700
                                              : 400,
                                          }}
                                        >
                                          {part.text}
                                        </span>
                                      ))}
                                    </span>
                                  )
                                }
                              />
                            </ListItemButton>
                          );
                        })
                      ) : (
                        <Box sx={{ height: 24 }}>
                          <Typography variant="body2">
                            No groups found
                          </Typography>
                        </Box>
                      )}
                    </SearchListWrapper>
                  </Stack>
                </Stack>
              ) : null}
            </Stack>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
